import { Button, makeStyles, Rating, RatingOnChangeEventData, Text, tokens } from "@fluentui/react-components";
import { EventHandler } from "@fluentui/react-utilities";
import React, { useEffect, useRef, useState } from "react";
import { DismissButton } from "../common/button/DismissButton";
import { FeedbackDialog } from "./FeedbackDialog";

type Props = {
  score?: number;
  onScoreSet?: (score: number) => void;
  onCommentSubmit?: (comment: string) => void;
  messages?: { role: string; type?: string; }[];
  feedbacks?: { threadFeedback?: () => { score?: number; } | undefined; };
  isMutating?: boolean;
  waitingFirstResponse?: boolean;
  onRateChanged?: (score: number) => void;
};

export const ThreadFeedbackArea: React.FC<Props> = (
  {
    score = 0,
    onScoreSet = () => {},
    onCommentSubmit = () => {},
    messages = [],
    feedbacks = null,
    isMutating = false,
    waitingFirstResponse = false,
    onRateChanged = () => {},
  },
) => {
  const styles = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showFeedbackArea, setShowFeedbackArea] = useState(false);
  const [feedbackTimerId, setFeedbackTimerId] = useState<NodeJS.Timeout | null>(null);
  const [showRatingSent, setShowRatingSent] = useState(false);

  // スクロールの制御
  const areaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (areaRef.current) {
      areaRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMutating, showFeedbackArea, showRatingSent]); // Dependencies for scrolling

  // フィードバックエリアの表示を制御するためのタイマー
  const feedbackDelay = 2 * 1000; // 2 seconds
  useEffect(() => {
    let currentTimerId = feedbackTimerId;

    if (currentTimerId) {
      clearTimeout(currentTimerId);
      setFeedbackTimerId(null);
    }
    setShowFeedbackArea(false);

    if (messages && messages.length > 0 && feedbacks && !isMutating && !waitingFirstResponse) {
      const lastMessage = messages[messages.length - 1];
      const hasThreadFeedback = !!feedbacks.threadFeedback?.(); // Check if thread feedback exists

      if (
        !hasThreadFeedback
        && lastMessage.role === "assistant"
        && lastMessage.type !== "CHOICE"
      ) {
        const newTimerId = setTimeout(() => {
          setShowFeedbackArea(true);

          if (areaRef.current) {
            areaRef.current.scrollIntoView({ behavior: "smooth" });
          }
          setFeedbackTimerId(null);
        }, feedbackDelay); // 2 seconds
        setFeedbackTimerId(newTimerId);
        currentTimerId = newTimerId;
      }
    }

    return () => {
      if (currentTimerId) {
        clearTimeout(currentTimerId);
      }
    };
  }, [messages, feedbacks, isMutating, waitingFirstResponse]);

  const ratingSentHideDuration = 30000; // 30 seconds
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;
    if (ratingSentHideDuration && ratingSentHideDuration > 0) {
      timerId = setTimeout(() => {
        setShowRatingSent(false);
      }, ratingSentHideDuration);
      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [showRatingSent, ratingSentHideDuration]);

  const handleRateSelect: EventHandler<RatingOnChangeEventData> = (event, data) => {
    if (!data.value) return;
    onScoreSet(data.value);
    setShowRatingSent(true);
    onRateChanged(data.value); // RateChangedイベントを発火
  };
  const handleRatingDismiss = () => {
    setShowFeedbackArea(false);
    if (feedbackTimerId) {
      clearTimeout(feedbackTimerId);
      setFeedbackTimerId(null);
    }
  };

  return (
    <>
      {showFeedbackArea
        && (
          <div className={styles.container}>
            <div className={styles.dismiss}>
              <DismissButton onClick={handleRatingDismiss}></DismissButton>
            </div>
            <Text className={styles.text}>
              ここまでのFAQチャットbotの評価はいかがですか？
            </Text>
            <Rating
              color="marigold"
              value={score}
              onChange={handleRateSelect}
            >
            </Rating>
          </div>
        )}
      {showRatingSent
        && (
          <div className={styles.container}>
            <div className={styles.dismiss}>
              <DismissButton onClick={() => setShowRatingSent(false)}></DismissButton>
            </div>
            <Text className={styles.text}>
              フィードバックを送信しました
            </Text>
            <Button
              appearance="transparent"
              className={styles.commentButton}
              onClick={() => setIsDialogOpen(true)}
            >
              評価をコメントする
            </Button>
          </div>
        )}
      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={(comment) => {
          onCommentSubmit(comment);
          setShowRatingSent(false);
          setIsDialogOpen(false);
        }}
      />
      <div ref={areaRef} />
    </>
  );
};

const useStyles = makeStyles({
  container: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
    width: "466px",
  },
  dismiss: {
    position: "absolute",
    top: tokens.spacingHorizontalS,
    right: tokens.spacingHorizontalS,
    cursor: "pointer",
  },
  text: {
    fontSize: tokens.fontSizeBase300,
  },
  commentButton: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});
